/** OAuth error payload returned by Keycloak token and authorize endpoints. */
export type KeycloakOAuthError = {
	error: string;
	error_description?: string;
};

const LAST_OAUTH_ERROR_STORAGE_KEY = 'apexx.auth.lastOAuthError';

type KeycloakNetworkError = Error & {
	response?: Response;
};

function getKeycloakErrorResponse(error: unknown): Response | undefined {
	if (typeof error !== 'object' || error === null || !('response' in error)) {
		return undefined;
	}

	const response = (error as KeycloakNetworkError).response;

	return response instanceof Response ? response : undefined;
}

/** Parses a Keycloak `NetworkError` (or similar) into a structured OAuth error when available. */
export async function parseKeycloakOAuthError(error: unknown): Promise<KeycloakOAuthError | undefined> {
	const response = getKeycloakErrorResponse(error);
	if (!response) {
		if (typeof error === 'object' && error !== null && 'error' in error) {
			const oauthError = error as KeycloakOAuthError;
			if (typeof oauthError.error === 'string' && oauthError.error !== '') {
				return oauthError;
			}
		}

		return undefined;
	}

	try {
		const body = (await response.clone().json()) as Partial<KeycloakOAuthError>;
		if (typeof body.error === 'string' && body.error !== '') {
			return {
				error: body.error,
				error_description:
					typeof body.error_description === 'string' ? body.error_description : undefined,
			};
		}
	} catch {
		return undefined;
	}

	return undefined;
}

/** Formats an OAuth error for logs and UI copy. */
export function formatKeycloakOAuthError(error: KeycloakOAuthError): string {
	return error.error_description
		? `${error.error}: ${error.error_description}`
		: error.error;
}

/** Persists the last OAuth callback / token exchange failure for the host app to display. */
export function saveLastKeycloakOAuthError(error: KeycloakOAuthError): void {
	if (typeof sessionStorage === 'undefined') {
		return;
	}

	sessionStorage.setItem(LAST_OAUTH_ERROR_STORAGE_KEY, JSON.stringify(error));
}

/** Reads and clears the last OAuth error saved during bootstrap. */
export function consumeLastKeycloakOAuthError(): KeycloakOAuthError | null {
	if (typeof sessionStorage === 'undefined') {
		return null;
	}

	const raw = sessionStorage.getItem(LAST_OAUTH_ERROR_STORAGE_KEY);
	sessionStorage.removeItem(LAST_OAUTH_ERROR_STORAGE_KEY);

	if (!raw) {
		return null;
	}

	try {
		const parsed = JSON.parse(raw) as Partial<KeycloakOAuthError>;
		if (typeof parsed.error === 'string' && parsed.error !== '') {
			return {
				error: parsed.error,
				error_description:
					typeof parsed.error_description === 'string' ? parsed.error_description : undefined,
			};
		}
	} catch {
		return null;
	}

	return null;
}
